// The cover catalog, ported unchanged from design_handoff_oreia/web/journals-web.jsx.
//
// A cover is a CSS gradient plus a procedural pattern layer — no images. That is what lets a
// notebook look bound rather than decorated, ship no assets, and sit correctly on both the dusk and
// the dawn theme. The ids are the contract with the server, which stores `cover_id` in plaintext and
// knows nothing else about what a cover looks like.

export type CoverPattern = 'none' | 'linen' | 'stars' | 'veil' | 'marble' | 'foil';

/**
 * The pack a cover belongs to, for the paid template packs in the design. Every cover ships
 * unlocked for now; the field is kept because it is what the store will read, and because the
 * catalog is the reference file's and should stay recognisable next to it.
 */
export type PackId = 'astral' | 'paper' | 'foil';

export interface CoverDesign {
  id: string;
  name: string;
  /** Text printed on the cover: light ink on a dark board, dark ink on a pale one. */
  ink: 'light' | 'dark';
  pattern: CoverPattern;
  /** A CSS background stack. */
  bg: string;
  packId?: PackId;
}

export const COVERS: CoverDesign[] = [
  {
    id: 'midnight',
    name: 'Midnight',
    ink: 'light',
    pattern: 'none',
    bg: 'linear-gradient(163deg, oklch(0.30 0.06 288), oklch(0.17 0.05 300))',
  },
  {
    id: 'orchid',
    name: 'Orchid',
    ink: 'light',
    pattern: 'none',
    bg: 'linear-gradient(150deg, oklch(0.52 0.13 320), oklch(0.30 0.09 285))',
  },
  {
    id: 'tide',
    name: 'Tide',
    ink: 'dark',
    pattern: 'none',
    bg: 'linear-gradient(160deg, oklch(0.90 0.06 220), oklch(0.90 0.06 158))',
  },
  {
    id: 'ember',
    name: 'Ember',
    ink: 'dark',
    pattern: 'none',
    bg: 'linear-gradient(155deg, oklch(0.89 0.07 68), oklch(0.83 0.09 32))',
  },
  {
    id: 'linen',
    name: 'Linen',
    ink: 'dark',
    pattern: 'linen',
    bg: 'linear-gradient(170deg, oklch(0.94 0.012 85), oklch(0.89 0.018 70))',
  },
  {
    id: 'slate',
    name: 'Slate',
    ink: 'light',
    pattern: 'linen',
    bg: 'linear-gradient(170deg, oklch(0.42 0.012 260), oklch(0.26 0.012 265))',
  },
  {
    id: 'nocturne',
    name: 'Nocturne',
    ink: 'light',
    pattern: 'stars',
    packId: 'astral',
    bg: 'radial-gradient(120% 90% at 70% 8%, oklch(0.42 0.09 290), transparent 62%), linear-gradient(165deg, oklch(0.20 0.04 285), oklch(0.12 0.03 290))',
  },
  {
    id: 'aurora',
    name: 'Aurora',
    ink: 'light',
    pattern: 'veil',
    packId: 'astral',
    bg: 'linear-gradient(155deg, oklch(0.44 0.11 250), oklch(0.52 0.12 190) 55%, oklch(0.34 0.09 300))',
  },
  {
    id: 'carrara',
    name: 'Carrara',
    ink: 'dark',
    pattern: 'marble',
    packId: 'paper',
    bg: 'linear-gradient(150deg, oklch(0.95 0.008 90), oklch(0.88 0.014 70))',
  },
  {
    id: 'porphyry',
    name: 'Porphyry',
    ink: 'light',
    pattern: 'marble',
    packId: 'paper',
    bg: 'linear-gradient(150deg, oklch(0.44 0.10 15), oklch(0.28 0.07 350))',
  },
  {
    id: 'gilt',
    name: 'Gilt',
    ink: 'light',
    pattern: 'foil',
    packId: 'foil',
    bg: 'linear-gradient(160deg, oklch(0.32 0.04 60), oklch(0.20 0.03 50))',
  },
  {
    id: 'verdigris',
    name: 'Verdigris',
    ink: 'light',
    pattern: 'foil',
    packId: 'foil',
    bg: 'linear-gradient(160deg, oklch(0.42 0.06 175), oklch(0.24 0.04 190))',
  },
];

export const DEFAULT_COVER_ID = 'midnight';

/**
 * Never null. A cover id that is not in the catalog — an older client's, a pack retired, a
 * hand-edited row — has to render as *something*, and a journal falling back to Midnight is a far
 * better failure than a shelf with a hole in it.
 */
export function coverById(id: string | null | undefined): CoverDesign {
  return COVERS.find((cover) => cover.id === id) ?? COVERS[0];
}

/** The six that come with the app, and the six the design sells as template packs. */
export const FREE_COVERS = COVERS.filter((cover) => !cover.packId);
export const TEMPLATE_COVERS = COVERS.filter((cover) => cover.packId);
